import { computed, nextTick, ref } from "vue";
import { defineStore } from "pinia";

import type { IndexSortOption } from "@/api/modules/autoQ/productSearch/types.ts";

import cloneDeep from "lodash/cloneDeep";

import { SortOptionName } from "@/api/modules/autoQ/productSearch/constants.ts";
import {
    useProductSearchResultsStore
} from "@/stores/productSearch/productSearchResultsStore";
import {
    INITIAL_SORT_OPTIONS,
    SORT_INDEX_START
} from "@/stores/productSearch/productSearchSortStore/constants";
import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";


export const useProductSearchSortStore = defineStore("productSearchSortStore", () => {
    const resultStore = useProductSearchResultsStore();
    const storageHelper = new StorageHelper(StorageName.ProductSearchSortStore);

    const sortOptions = ref<IndexSortOption[]>(cloneDeep(INITIAL_SORT_OPTIONS));

    const indexSortOptions = computed(() =>
        [...sortOptions.value].sort((a, b) => a.index - b.index)
    );

    const getSortOption = (sortOptionKey: string): IndexSortOption | undefined => {
        return sortOptions.value.find(({ key }) => key === sortOptionKey);
    };

    const sortOptionIsActive = (sortOptionKey: string): boolean => {
        return sortOptions.value.some(({ key }) => key === sortOptionKey);
    };

    const sortOptionIsDescending = (sortOptionKey: string): boolean | undefined => {
        const currentSortOption = getSortOption(sortOptionKey);

        if (!currentSortOption) {
            return undefined;
        }

        return currentSortOption.order === SortOptionName.Descending;
    };

    const addSortOption = (sortOption: IndexSortOption): void => {
        sortOptions.value = [cloneDeep(sortOption)];
    };

    const removeSortOption = (sortOptionKey: string): void => {
        const filteredOptions = sortOptions.value.filter(({ key }) => key !== sortOptionKey);

        sortOptions.value = filteredOptions.length ? filteredOptions : cloneDeep(INITIAL_SORT_OPTIONS);
    };

    const toggleSortOption = (sortOptionKey: string): void => {
        try {
            if (!sortOptionIsActive(sortOptionKey)) {
                addSortOption({
                    key: sortOptionKey,
                    order: SortOptionName.Descending,
                    index: SORT_INDEX_START
                });

                return;
            }

            addSortOption({
                key: sortOptionKey,
                order: sortOptionIsDescending(sortOptionKey) ? SortOptionName.Ascending : SortOptionName.Descending,
                index: SORT_INDEX_START
            });
        } finally {
            storageHelper.write(sortOptions.value);
        }
    };

    const currentIndex = (sortOptionKey: string) =>
        computed(() => getSortOption(sortOptionKey)?.index);

    const currentOrder = (sortOptionKey: string) =>
        computed(() => getSortOption(sortOptionKey)?.order);

    const sortOptionsPayload = computed(() => indexSortOptions.value.map(({
        key,
        order
    }) => ({
        key,
        order
    })));

    const toggleAndReloadSortOption = async (sortOptionKey: string): Promise<void> => {
        toggleSortOption(sortOptionKey);

        await resultStore.fetchResults();
    };

    const reset = async () => {
        sortOptions.value = cloneDeep(INITIAL_SORT_OPTIONS);

        await nextTick();

        return Promise.resolve();
    };

    const initialize = async () => {
        const savedSortOptions = storageHelper.read<IndexSortOption[]>();

        if (!savedSortOptions || !savedSortOptions.length) return;

        sortOptions.value = savedSortOptions;

        await nextTick();

        return Promise.resolve();
    };


    return {
        sortOptions,
        indexSortOptions,
        sortOptionIsActive,
        sortOptionIsDescending,
        addSortOption,
        removeSortOption,
        toggleSortOption: toggleAndReloadSortOption,
        currentIndex,
        currentOrder,
        sortOptionsPayload,
        reset,
        initialize
    };
});